export const FONT_OPTIONS = [
  "Chivo", "Inter", "Barlow Condensed", "Oswald", "Archivo", "Manrope",
  "Montserrat", "Public Sans", "Roboto Condensed", "Source Sans 3", "Work Sans", "DM Sans",
];

export function hexToHsl(hex) {
  if (!hex) return null;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, sat = 0;
  const light = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = light > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: hue = (b - r) / d + 2; break;
      default: hue = (r - g) / d + 4;
    }
    hue *= 60;
  }
  return `${Math.round(hue)} ${Math.round(sat * 100)}% ${Math.round(light * 100)}%`;
}

function lightenHsl(hslTriplet, amount = 6) {
  if (!hslTriplet) return null;
  const parts = hslTriplet.match(/(\d+)\s+(\d+)%\s+(\d+)%/);
  if (!parts) return hslTriplet;
  const l = Math.min(95, parseInt(parts[3], 10) + amount);
  return `${parts[1]} ${parts[2]}% ${l}%`;
}

const RADIUS_MAP = { none: "0", sm: "0.25rem", md: "0.5rem", lg: "0.9rem" };
const BTN_RADIUS_MAP = { square: "2px", rounded: "0.5rem", pill: "9999px" };

function loadGoogleFonts(fonts) {
  const unique = [...new Set(fonts.filter(Boolean))];
  if (!unique.length) return;
  const id = "cms-google-fonts";
  const href = `https://fonts.googleapis.com/css2?${unique
    .map((f) => `family=${f.replace(/ /g, "+")}:wght@400;500;600;700;900`)
    .join("&")}&display=swap`;
  let link = document.getElementById(id);
  if (!link) {
    link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  link.href = href;
}

export function applyTheme(theme) {
  if (!theme) return;
  const root = document.documentElement;
  const setHsl = (name, hex) => {
    const hsl = hexToHsl(hex);
    if (hsl) root.style.setProperty(name, hsl);
  };
  setHsl("--primary", theme.primary_color);
  const primaryHsl = hexToHsl(theme.primary_color);
  if (primaryHsl) root.style.setProperty("--primary-hover", lightenHsl(primaryHsl));
  setHsl("--accent", theme.accent_color);
  setHsl("--background", theme.background_color);
  setHsl("--foreground", theme.text_color);
  if (theme.border_radius && RADIUS_MAP[theme.border_radius] != null) {
    root.style.setProperty("--radius", RADIUS_MAP[theme.border_radius]);
  }
  root.style.setProperty("--btn-radius", BTN_RADIUS_MAP[theme.button_style] || "2px");
  if (theme.heading_font) root.style.setProperty("--font-display", `'${theme.heading_font}'`);
  if (theme.body_font) root.style.setProperty("--font-body", `'${theme.body_font}'`);
  loadGoogleFonts([theme.heading_font, theme.body_font]);
  if (theme.favicon_url) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = theme.favicon_url;
  }
}

export function setMetaTag(name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function setMetaProperty(property, content) {
  if (!content) return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function setCanonical(url) {
  if (!url) return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = url;
}
